import { useEffect, useRef, useState, useContext } from "react"
import styled from "styled-components"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import { Document as Pdf, Page, pdfjs } from "react-pdf"
import AcLoading from "~/components/atoms/AcLoading"
import AcIcon from "../atoms/AcIcon"
import { useCallVideoState } from "~/composable/useCallVideoStateZoom"
import { Json, MessageType, UserType } from "../template/video/Meet"
import { selectTemporary } from "~/slices/temporarySlice"
import { useSelector } from "react-redux"
import { VideoContext } from "~/components/twilio/VideoProvider"
import { DATA_KEY } from "~/composable/useCallVideoStateZoom"
import * as Sentry from "@sentry/browser"

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
}

const scales = [0.75, 0.8, 0.9, 1, 1.1, 1.2, 1.25, 1.3, 1.4, 1.5, 1.75, 2.0, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4]
const normalizeZoomPercent = (percent: number) => Number(percent.toFixed(2))

const PdfViewerMeeting = () => {
  const { sendData, mediaStream, client } = useContext(VideoContext)
  const { userType } = useSelector(selectTemporary)
  const { handleChangeTab, pdfUrl } = useCallVideoState()
  const [numPages, setNumPages] = useState(0)
  const [scaleIndex, setScaleIndex] = useState(3)
  const [zoomPercent, setZoomPercent] = useState(100)
  const [pageNumber, setPageNumber] = useState(1)
  const mainPdfRef = useRef<HTMLDivElement>(null)

  const onDocumentLoadSuccess = (data) => {
    setNumPages(data.numPages)
  }

  const onPage = (page: number) => {
    setPageNumber(page)
    sendData({
      type: MessageType.NOTIFY,
      data: {
        [DATA_KEY.CHANGE_PAGE_NUMBER]: page,
      },
    })
  }

  const handleScroll = (event) => {
    if (userType === UserType.AGENT) {
      const toggleScroll = JSON.parse(sessionStorage.getItem("toggleScroll") ?? "-1")
      if (toggleScroll === 1 || toggleScroll === -1) {
        const { scrollLeft, scrollTop } = event.currentTarget
        sendData({
          type: MessageType.NOTIFY,
          data: {
            [DATA_KEY.SCROLL]: { scrollLeft, scrollTop },
          },
        })
      } else {
        sessionStorage.setItem("toggleScroll", "1")
      }
    }
  }

  const onZoom = (index) => {
    const element = document.getElementsByClassName("viewer")[0] as HTMLElement
    const currWidth = element.style.width

    if (!currWidth) {
      const width = element.clientWidth - 2
      const height = element.clientHeight - 2
      element.style.width = `${width}px`
      element.style.height = `${height}px`
    }

    const scaleSum = scaleIndex + index
    if (scaleSum >= 0 && scaleSum < scales.length) {
      const nextZoomPercent = normalizeZoomPercent(scales[scaleSum] * 100)
      setScaleIndex(scaleSum)
      setZoomPercent(nextZoomPercent)
      sendData({
        type: MessageType.NOTIFY,
        data: {
          [DATA_KEY.PDF_MUTATION]: {
            zoomPercent: nextZoomPercent,
            scale: scaleSum,
          },
        },
      })
    }
  }

  const onReceive = (response: Json) => {
    if (response.type === MessageType.NOTIFY) {
      const keys = Object.keys(response.data)

      if (keys.includes(DATA_KEY.PDF_MUTATION)) {
        setScaleIndex(response.data.pdfMutation.scale)
        setZoomPercent(normalizeZoomPercent(response.data.pdfMutation.zoomPercent))
      }

      if (keys.includes(DATA_KEY.CHANGE_PAGE_NUMBER)) {
        setPageNumber(response.data.changePageNumber)
      }

      if (keys.includes(DATA_KEY.SCROLL)) {
        if (mainPdfRef.current) {
          mainPdfRef.current.scrollTo({
            top: response.data.scroll.scrollTop,
            left: response.data.scroll.scrollLeft,
          })
          if (userType === UserType.AGENT) {
            sessionStorage.setItem("toggleScroll", "0")
          }
        }
      }
    }
  }

  useEffect(() => {
    if (client && mediaStream) {
      client.on("chat-on-message", (payload) => {
        try {
          const { message } = payload
          onReceive(JSON.parse(message))
        } catch (e) {
          console.error(e)
          Sentry.captureException(e)
        }
      })
    }
  }, [client, mediaStream])

  useEffect(() => {
    setPageNumber(1)
  }, [pdfUrl])

  return (
    <Preview>
      {pdfUrl && (
        <>
          {userType === UserType.AGENT ? (
            <PageList file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} options={options}>
              {[...new Array(numPages)].map((_, index) => {
                const currPage = index + 1
                return (
                  <PageItem
                    key={index}
                    pageNumber={currPage}
                    onClick={() => onPage(currPage)}
                    isBorder={pageNumber === currPage}
                    width={140}
                    height={200}
                  />
                )
              })}
            </PageList>
          ) : (
            <div className="col-1" />
          )}
          <PdfContainer
            isAgent={userType === UserType.AGENT}
            screenWidth={window.innerWidth}
            screenHeight={window.innerHeight}
          >
            <Actions className="actions">
              <Zoom className="notranslate" translate="no">
                <span>{zoomPercent}</span>
                <span>&nbsp;%</span>
              </Zoom>
              {userType === UserType.AGENT && (
                <>
                  <Button onClick={() => onZoom(1)}>
                    <AcIcon name="circle-plus" />
                    <span>拡大</span>
                  </Button>
                  <Button onClick={() => onZoom(-1)}>
                    <AcIcon name="circle-minus" />
                    <span>縮小</span>
                  </Button>
                  <ButtonChange onClick={() => handleChangeTab(2)}>
                    <AcIcon name="share-from-square" />
                    <span>書類切替</span>
                  </ButtonChange>
                </>
              )}
            </Actions>
            <Pdf
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              options={options}
              className={"viewer"}
              onScroll={handleScroll}
              inputRef={mainPdfRef}
            >
              <Page
                pageNumber={pageNumber}
                scale={scales[scaleIndex]}
                width={window.innerWidth > 520 ? 520 : window.innerWidth - 20}
              />
            </Pdf>
          </PdfContainer>
        </>
      )}
      {!pdfUrl && (
        <AcLoading
          isOverlay={true}
          message={"PDFファイルを読み込んでいます、しばらくお待ちください。"}
        />
      )}
    </Preview>
  )
}

export default PdfViewerMeeting

const Preview = styled.div`
  height: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 200px 1fr;
  padding: 20px 0 20px 10px !important;
  color: white;
  .internalLink {
    display: none;
  }
  @media screen and (max-width: 960px) {
    grid-template-columns: 1fr;
    .col-1 {
      display: none;
    }
  }
  @media screen and (max-width: 740px) {
    padding: 0 !important;
    .actions {
      display: none;
    }
  }
`

const PdfContainer = styled.div`
  max-width: 630px;
  margin: 0 auto;
  max-height: 740px;
  display: flex;
  justify-content: center;
  .react-pdf__Page {
    .react-pdf__Page__textContent {
      display: none;
    }
  }

  .viewer {
    overflow: ${({ isAgent }) => (isAgent ? "auto" : "hidden")};
    @media screen and (max-width: 650px) {
      width: ${({ screenWidth }) => `${screenWidth - 20}px`};
      height: ${({ screenHeight }) => `calc(${screenHeight}px - 200px)`};
      -webkit-height: ${({ screenHeight }) => `calc(${screenHeight}px - 200px)`};
      -moz-height: ${({ screenHeight }) => `calc(${screenHeight}px - 200px)`};
      -ms-height: ${({ screenHeight }) => `calc(${screenHeight}px - 200px)`};
      -o-height: ${({ screenHeight }) => `calc(${screenHeight}px - 200px)`};
    }
    .react-pdf__Page {
      background-color: transparent !important;
    }
  }
`
const PageList = styled(Pdf)`
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  @media screen and (max-width: 960px) {
    display: none;
  }
`

const PageItem = styled(Page)`
  margin-bottom: 20px;
  cursor: pointer;

  ::after {
    content: ${({ pageNumber }) => pageNumber && `'${pageNumber}'`};
    ${({ isBorder }) => isBorder && `color: #6B6BF9;`}
    position: absolute;
    top: 0;
    left: 0;
    font-size: 14px;
  }

  .react-pdf__Page__canvas {
    transform: translateX(25px);
    ${({ isBorder }) => isBorder && `border: 6px solid #6B6BF9;`}
  }

  background-color: transparent !important;
  .textLayer {
    display: none;
  }
`

const Actions = styled.div`
  margin-right: 15px;
  width: 70px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Button = styled.button`
  width: 100%;
  height: 28px;
  border-radius: 28px;
  background-color: #6b6bf9;
  display: flex;
  align-items: center;
  i {
    margin: 0 5px;
  }
  margin-top: 10px;
  span {
    font-size: 13px;
  }
`

const ButtonChange = styled(Button)`
  height: 70px;
  border-radius: 10px;
  margin-top: auto;
  flex-direction: column;
  justify-content: center;
`

const Zoom = styled.div`
  border: 2px solid #ddddff;
  width: 68px;
  height: 28px;
  text-align: center;
`
